import React, { useState } from "react";
import { Button, Flex, Select } from "@jsq/ds/core";
import { ConfirmSubmitModal } from "./modals/ConfirmSubmitModal";
import { ReturnModal } from "./modals/ReturnModal";
import * as styles from "./styles_vanilla.css";
import { usePostBtsComment } from "./usePostBtsComment";
import type { TaskGroupOverride } from "./usePostBtsComment";
import type { TaskStepDetail } from "../../types";

interface CheckerActionBarProps {
  task: TaskStepDetail;
  actionLoading: boolean;
  /**
   * True when the task is unassigned OR assigned to someone other than the
   * logged-in user. Locks the reviewer-conclusion select + Submit so the BE
   * never receives an action from a non-assignee.
   */
  isNotAssignee?: boolean;
  onActionClick: (action: string, payload?: Record<string, unknown>) => void;
  /** Arena id of the workflow — forwarded to `ConfirmSubmitModal` for its GP user dropdown. */
  arenaId?: number;
  /** JSM issue key used to post the note as a BTS comment alongside the action. */
  issueKey?: string;
  /** Next task in the workflow — when present, the approve-with-GP-approval
   *  comment is tagged with this task's group so it surfaces on the GP step. */
  nextTask?: TaskGroupOverride;
}

type ReviewerConclusion = "" | "approve" | "reject";

export const CheckerActionBar: React.FC<CheckerActionBarProps> = ({
  task,
  actionLoading,
  isNotAssignee = false,
  onActionClick,
  arenaId,
  issueKey,
  nextTask,
}) => {
  const postComment = usePostBtsComment(issueKey, task);
  const [conclusion, setConclusion] = useState<ReviewerConclusion>("");
  const [returnOpen, setReturnOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset local state when the user navigates to a different task — same
  // derived-state-during-render pattern OnBehalfActionBar uses, sidesteps the
  // react-hooks/set-state-in-effect cascade-render flag.
  const [prevTaskId, setPrevTaskId] = useState(task.id);
  if (prevTaskId !== task.id) {
    setPrevTaskId(task.id);
    setConclusion("");
    setReturnOpen(false);
    setConfirmOpen(false);
  }

  const canApprove = task.availableActions.some((a) => a.action === "approve");
  const canReject = task.availableActions.some((a) => a.action === "reject");

  const submitDisabled = actionLoading || !conclusion || isNotAssignee;

  const handleSubmit = () => {
    if (conclusion === "reject") {
      setReturnOpen(true);
    } else if (conclusion === "approve") {
      if (task.requiresGpApproval) {
        // MC+GP task — open the GP assignment modal before approving
        setConfirmOpen(true);
      } else {
        // MC-only task (e.g. capital call Calculation, IS Setup) — approve directly
        onActionClick("approve", {});
      }
    }
  };

  const handleReturnConfirm = (note: string) => {
    setReturnOpen(false);
    setConclusion("");
    onActionClick("reject", { note });
    postComment(note, true);
  };

  // "Confirm task is complete" closes the MC task; the workflow engine spawns
  // the next GP task (Transaction Approval / NAV Approval / Payment Release)
  // and applies `nextAssigneeId` as the spawned task's assignee (BE-side work
  // tracked separately — until that lands, the GP user picked here is captured
  // in the approve mutation's payload but doesn't reach the spawned task).
  // assigneeId arrives as a string from useGpUsers; coerce to Int and skip
  // empty so we never send a phantom user id to BE.
  const handleConfirmSubmit = (note: string, assigneeId: string) => {
    setConfirmOpen(false);
    setConclusion("");
    const trimmed = assigneeId.trim();
    const parsed = trimmed === "" ? NaN : Number(trimmed);
    const nextAssigneeId = Number.isFinite(parsed) ? parsed : undefined;
    onActionClick("approve", { note, nextAssigneeId });
    postComment(note, false, nextTask);
  };

  return (
    <>
      <Flex gap="8px" alignItems="center">
        <div className={styles.reviewerSelect}>
          <Select
            value={conclusion}
            onChange={(v) => setConclusion(String(v) as ReviewerConclusion)}
            full
            position="up"
            label="Reviewer conclusion"
            hideLabel
            placeholder="Select reviewer conclusion"
            disabled={isNotAssignee}
            data-testid="checker-conclusion-select"
          >
            <Select.Section>
              {
                // DS Select.Section walks children with React.Children.forEach
                // and dereferences child.props without guarding against falsy
                // entries (see @jsq/ds/core Select). Build the option list as
                // a typed array with no falsy slots so conditional rendering
                // never emits `false` / `null` siblings inside the section.
                ((): React.ReactElement[] => {
                  const opts: React.ReactElement[] = [];
                  if (canApprove) {
                    opts.push(
                      <Select.Option key="approve" value="approve">
                        Confirm task is complete
                      </Select.Option>,
                    );
                  }
                  if (canReject) {
                    opts.push(
                      <Select.Option key="reject" value="reject">
                        Return
                      </Select.Option>,
                    );
                  }
                  return opts;
                })()
              }
            </Select.Section>
          </Select>
        </div>
        <Button
          variant="primary"
          disabled={submitDisabled}
          onClick={handleSubmit}
          data-testid="checker-submit"
        >
          Submit
        </Button>
      </Flex>
      <ReturnModal
        isOpen={returnOpen}
        loading={actionLoading}
        onCancel={() => setReturnOpen(false)}
        onConfirm={handleReturnConfirm}
      />
      <ConfirmSubmitModal
        isOpen={confirmOpen}
        task={task}
        arenaId={arenaId}
        loading={actionLoading}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
      />
    </>
  );
};

export default CheckerActionBar;
